import {
  Component,
  ElementRef,
  Optional,
  Self,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  ControlValueAccessor,
  NgControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import {
  COUNTRIES,
  DEFAULT_COUNTRY,
  formatPhoneByCountry,
  getCountryByCode,
  validatePhoneByCountry,
  type Country,
} from '../../../features/contact/utils/countries.util';

/** Valor exposto ao FormControl que consome este componente. */
export interface PhoneCountryValue {
  countryCode: string;
  phoneNumber: string;
}

/**
 * Validador do telefone+país. Lógica centralizada aqui e anexada pelo control
 * do componente pai (`new FormControl(value, [phoneCountryValidator])`).
 *
 * Não é provido via `NG_VALIDATORS` de propósito: o componente faz
 * self-registration do `NgControl` (para exibir os erros internamente) e
 * adicionar `NG_VALIDATORS` recriaria o ciclo de DI que esse padrão evita.
 */
export const phoneCountryValidator: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  const value = control.value as PhoneCountryValue | null;
  const phone = value?.phoneNumber ?? '';
  if (!phone) {
    return { required: true };
  }
  const country = getCountryByCode(value?.countryCode ?? '') ?? DEFAULT_COUNTRY;
  if (!validatePhoneByCountry(phone, country)) {
    return { invalid: true };
  }
  return null;
};

/**
 * Input de telefone com seletor de país. Encapsula formatação, validação e o
 * dropdown de países, expondo-se como um único FormControl via
 * `ControlValueAccessor` + `Validator`. Consome com `formControlName`/`formControl`.
 */
@Component({
  selector: 'app-phone-country-input',
  imports: [CommonModule],
  templateUrl: './phone-country-input.component.html',
  styleUrl: './phone-country-input.component.scss',
  host: {
    '(document:click)': 'onDocumentClick($event)',
  },
})
export class PhoneCountryInputComponent implements ControlValueAccessor {
  /** Id aplicado ao input, para associar a um `<label for>` no componente pai. */
  readonly inputId = input<string>();

  private readonly countrySelector =
    viewChild<ElementRef<HTMLElement>>('countrySelector');

  readonly countries = COUNTRIES;
  readonly selectedCountry = signal<Country>(DEFAULT_COUNTRY);
  readonly isCountryDropdownOpen = signal(false);
  readonly phoneNumber = signal('');
  readonly disabled = signal(false);

  private onChange: (value: PhoneCountryValue) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(@Self() @Optional() private readonly ngControl: NgControl) {
    // Self-register como value accessor evita o ciclo de DI do NG_VALUE_ACCESSOR.
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  /** Controle hospedeiro, usado pelo template para exibir mensagens de erro. */
  get control(): AbstractControl | null {
    return this.ngControl?.control ?? null;
  }

  // --- ControlValueAccessor ---

  writeValue(value: PhoneCountryValue | null): void {
    const country = getCountryByCode(value?.countryCode ?? '') ?? DEFAULT_COUNTRY;
    this.selectedCountry.set(country);
    this.phoneNumber.set(value?.phoneNumber ?? '');
  }

  registerOnChange(fn: (value: PhoneCountryValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  // --- UI ---

  toggleCountryDropdown(): void {
    this.isCountryDropdownOpen.update((open) => !open);
  }

  onCountrySelect(country: Country): void {
    this.selectedCountry.set(country);
    this.isCountryDropdownOpen.set(false);

    const digits = this.phoneNumber().replace(/\D/g, '');
    this.phoneNumber.set(formatPhoneByCountry(digits, country));
    this.emit();
  }

  formatPhoneNumber(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '');
    const formatted = formatPhoneByCountry(digits, this.selectedCountry());
    this.phoneNumber.set(formatted);
    // Mantém o DOM em sincronia mesmo quando `formatted` não muda (dígito além
    // do limite ou caractere inválido): o binding [value] só reescreve quando o
    // valor muda, então sem isto o caractere descartado ficaria visível.
    input.value = formatted;
    this.emit();
  }

  onBlur(): void {
    this.onTouched();
  }

  onDocumentClick(event: MouseEvent): void {
    if (!this.isCountryDropdownOpen()) {
      return;
    }
    const selector = this.countrySelector();
    if (selector && !selector.nativeElement.contains(event.target as Node)) {
      this.isCountryDropdownOpen.set(false);
    }
  }

  private emit(): void {
    this.onChange({
      countryCode: this.selectedCountry().code,
      phoneNumber: this.phoneNumber(),
    });
  }
}
